// src/pages/expense/env/total/ExpenseEnvTotalList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../../../api";
import styles from "./ExpenseEnvTotalList.module.css";
import useHighlightFromQuery from "../../../../hooks/useHighlightFromQuery";

export default function ExpenseEnvTotalList() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.title = "(구) 환경/종합 지출목록";
    if (!location.state?.tabTitle) {
      navigate({ pathname: ".", search: location.search }, {
        replace: true,
        state: { ...(location.state || {}), tabTitle: "(구) 환경/종합 지출목록" },
      });
    }
  }, []); // eslint-disable-line

  const year = new Date().getFullYear();
  const [fromDate, setFromDate] = useState(`${year}-01-01`);
  const [toDate, setToDate] = useState(`${year}-12-31`);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // ✅ 상세보기 모달
  const [selectedRow, setSelectedRow] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  // ✅ 목록 불러오기
  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const params = {
          search: search || undefined,
          ordering: "-draft_date,-seq",
        };
        const res = await api.get("/expense/env/total/", { params });
        const list = Array.isArray(res.data) ? res.data : res.data.results || [];
        if (!ignore) setRows(list);
      } catch (e) {
        console.error(e);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [search]);

  // ✅ 기간 필터
  const filtered = useMemo(() => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    to.setHours(23, 59, 59, 999);

    return rows.filter((r) => {
      const d = new Date(r.execution_date);
      return d >= from && d <= to;
    });
  }, [rows, fromDate, toDate]);

  // ✅ 합계 계산 (공급가액, 부가세 추가)
  const sums = useMemo(() => {
    let supply = 0;
    let vat = 0;
    let total = 0;
    let transfer = 0;
    let card = 0;
    let cash = 0;

    filtered.forEach((r) => {
      const supplyAmt = Number(r.supply_amount) || 0;
      const vatAmt = Number(r.vat) || 0;
      const totalAmt = supplyAmt + vatAmt;

      supply += supplyAmt;
      vat += vatAmt;
      total += totalAmt;

      if (r.pay_method === "transfer") transfer += totalAmt;
      if (r.pay_method === "card") card += totalAmt;
      if (r.pay_method === "cash") cash += totalAmt;
    });

    return { supply, vat, total, transfer, card, cash };
  }, [filtered]);

  // ✅ 엑셀 다운로드
  const downloadExcel = () => {
    alert("📌 엑셀 다운로드 API 준비 필요함.");
  };

  // ✅ 상세보기 열기
  const openDetail = (row) => {
    console.log("📋 상세보기 데이터:", row);
    console.log("📋 거래처 정보:", row.partners);
    console.log("📋 품목 정보:", row.items);
    console.log("📋 인건비 정보:", row.salaries);
    setSelectedRow(row);
    setShowDetail(true);
  };

  // ✅ 원본 데이터 열기(?highlight=id) — 대상 행을 찾아 스크롤+하이라이트, 상세 모달까지 자동 오픈
  const isHighlighted = useHighlightFromQuery(filtered, { setFromDate, setToDate, onFound: openDetail });

  // ✅ 상세보기 닫기
  const closeDetail = () => {
    setShowDetail(false);
    setSelectedRow(null);
  };

  // ✅ 거래처 목록 표시
  const getPartnerNames = (row) => {
    if (row.partners && Array.isArray(row.partners) && row.partners.length > 0) {
      return row.partners.map(p => p.name).filter(Boolean).join(", ");
    }
    return row.partner_name || "-";
  };

  // ✅ 상세보기에서 사용할 거래처 배열 가져오기
  const getPartnerList = (row) => {
    if (row.partners && Array.isArray(row.partners) && row.partners.length > 0) {
      return row.partners;
    }
    // partners 배열이 없으면 partner_name을 배열로 변환
    if (row.partner_name) {
      return [{ name: row.partner_name, id: row.partner_id }];
    }
    return [];
  };

  if (loading) return <div style={{ padding: 16 }}>불러오는 중…</div>;

  return (
    <div style={{ padding: 16 }}>
      <h2>(구) 환경/종합 지출목록</h2>

      {/* 🔍 필터 바 */}
      <div className={styles.filterBar}>
        <div className={styles.filterItem}>
          <div className={styles.label}>기간</div>
          <div className={styles.dateRow}>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className={styles.inputBase}
            />
            <span className={styles.dateDash}>~</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className={styles.inputBase}
            />
          </div>
        </div>

        <div className={styles.filterItem}>
          <div className={styles.label}>검색</div>
          <div className={styles.searchRow}>
            <input
              placeholder="기안번호/거래처/제목/비고 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && setSearch(searchInput.trim())}
              className={`${styles.inputBase} ${styles.inputSearch}`}
            />
            <button onClick={() => setSearch(searchInput.trim())} className={styles.btn}>
              검색
            </button>
            <button
              onClick={() => {
                setSearch("");
                setSearchInput("");
                setFromDate(`${year}-01-01`);
                setToDate(`${year}-12-31`);
              }}
              className={styles.btn}
            >
              초기화
            </button>
            <button onClick={downloadExcel} className={styles.btnPrimary}>
              엑셀 다운로드
            </button>
          </div>
        </div>
      </div>

      {/* ✅ 테이블 (공급가액, 부가세 컬럼 추가) */}
      <table className={styles.table}>
        <thead>
          <tr>
            <th>작성일</th>
            <th>기안번호</th>
            <th>거래처</th>
            <th>제목</th>
            <th>총금액</th>
            <th>공급가액</th>
            <th>부가세</th>
            <th>계좌이체</th>
            <th>카드결제</th>
            <th>현금결제</th>
            <th>비고</th>
            <th>상세</th>
          </tr>
        </thead>

        <tbody>
          {/* ✅ 합계 행 (공급가액, 부가세 추가) */}
          <tr style={{ background: "#fafafa", fontWeight: "bold" }}>
            <td colSpan="4" style={{ textAlign: "center" }}>합계</td>
            <td style={{ textAlign: "right" }}>{sums.total.toLocaleString()}</td>
            <td style={{ textAlign: "right" }}>{sums.supply.toLocaleString()}</td>
            <td style={{ textAlign: "right" }}>{sums.vat.toLocaleString()}</td>
            <td style={{ textAlign: "right" }}>{sums.transfer.toLocaleString()}</td>
            <td style={{ textAlign: "right" }}>{sums.card.toLocaleString()}</td>
            <td style={{ textAlign: "right" }}>{sums.cash.toLocaleString()}</td>
            <td></td>
            <td></td>
          </tr>

          {/* ✅ 데이터 행 (공급가액, 부가세 추가) */}
          {filtered.map((r) => {
            const supplyAmt = Number(r.supply_amount) || 0;
            const vatAmt = Number(r.vat) || 0;
            const totalAmt = supplyAmt + vatAmt;

            return (
              <tr
                key={r.id}
                id={`row-${r.id}`}
                style={isHighlighted(r.id) ? { background: "#fff59d", transition: "background 0.6s" } : undefined}
              >
                <td>{r.execution_date}</td>
                <td>{r.draft_no}</td>
                <td>{getPartnerNames(r)}</td>
                <td>{r.title}</td>
                <td style={{ textAlign: "right" }}>{totalAmt.toLocaleString()}</td>
                <td style={{ textAlign: "right" }}>{supplyAmt.toLocaleString()}</td>
                <td style={{ textAlign: "right" }}>{vatAmt.toLocaleString()}</td>
                <td style={{ textAlign: "right" }}>
                  {r.pay_method === "transfer" ? totalAmt.toLocaleString() : ""}
                </td>
                <td style={{ textAlign: "right" }}>
                  {r.pay_method === "card" ? totalAmt.toLocaleString() : ""}
                </td>
                <td style={{ textAlign: "right" }}>
                  {r.pay_method === "cash" ? totalAmt.toLocaleString() : ""}
                </td>
                <td>{r.remark}</td>
                <td>
                  <button 
                    onClick={() => openDetail(r)}
                    className={styles.btnDetail}
                  >
                    보기
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ✅ 상세보기 모달 */}
      {showDetail && selectedRow && (
        <div className={styles.modalOverlay} onClick={closeDetail}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>지출 상세 내역</h3>
              <button onClick={closeDetail} className={styles.closeBtn}>✕</button>
            </div>

            <div className={styles.modalBody}>
              {/* 기본 정보 */}
              <div className={styles.section}>
                <h4>기본 정보</h4>
                <table className={styles.detailTable}>
                  <tbody>
                    <tr>
                      <th>기안번호</th>
                      <td>{selectedRow.draft_no}</td>
                      <th>작성일</th>
                      <td>{selectedRow.draft_date}</td>
                    </tr>
                    <tr>
                      <th>집행예정일</th>
                      <td>{selectedRow.execution_date}</td>
                      <th>거래방법</th>
                      <td>
                        {selectedRow.pay_method === "transfer" && "계좌이체"}
                        {selectedRow.pay_method === "card" && "카드결제"}
                        {selectedRow.pay_method === "cash" && "현금결제"}
                      </td>
                    </tr>
                    <tr>
                      <th>제목</th>
                      <td colSpan="3">{selectedRow.title}</td>
                    </tr>
                    <tr>
                      <th>사용내역</th>
                      <td colSpan="3">
                        {selectedRow.category_name || '-'} / {selectedRow.item_name || '-'} / {selectedRow.field_name || '-'}
                        {selectedRow.other_count > 0 && ` (외 ${selectedRow.other_count}종)`}
                      </td>
                    </tr>
                    <tr>
                      <th>비고</th>
                      <td colSpan="3">{selectedRow.remark || "-"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 거래처 정보 */}
              {(() => {
                const partnerList = getPartnerList(selectedRow);
                return partnerList.length > 0 && (
                  <div className={styles.section}>
                    <h4>거래처 정보</h4>
                    <table className={styles.itemTable}>
                      <thead>
                        <tr>
                          <th style={{ width: "80px" }}>순번</th>
                          <th>거래처명</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partnerList.map((p, idx) => (
                          <tr key={idx}>
                            <td style={{ textAlign: "center" }}>{idx + 1}</td>
                            <td>{p.name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* 품목 정보 */}
              {selectedRow.items && selectedRow.items.length > 0 && (
                <div className={styles.section}>
                  <h4>품목 내역</h4>
                  <table className={styles.itemTable}>
                    <thead>
                      <tr>
                        <th>품목명</th>
                        <th>단위</th>
                        <th>수량</th>
                        <th>단가</th>
                        <th>금액</th>
                        <th>공급가액</th>
                        <th>부가세</th>
                        <th>비고</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRow.items.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.name}</td>
                          <td>{item.unit}</td>
                          <td style={{ textAlign: "right" }}>{item.qty?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{item.price?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{item.total?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{item.supply?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{item.vat?.toLocaleString()}</td>
                          <td>{item.remark}</td>
                        </tr>
                      ))}
                      <tr className={styles.sumRow}>
                        <td colSpan="4">합계</td>
                        <td style={{ textAlign: "right" }}>
                          {selectedRow.items.reduce((a, b) => a + (b.total || 0), 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {selectedRow.items.reduce((a, b) => a + (b.supply || 0), 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {selectedRow.items.reduce((a, b) => a + (b.vat || 0), 0).toLocaleString()}
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* 인건비 정보 */}
              {selectedRow.salaries && selectedRow.salaries.length > 0 && (
                <div className={styles.section}>
                  <h4>인건비 내역</h4>
                  <table className={styles.itemTable}>
                    <thead>
                      <tr>
                        <th>이름</th>
                        <th>급여합계</th>
                        <th>소득세</th>
                        <th>지방소득세</th>
                        <th>건강보험</th>
                        <th>요양보험</th>
                        <th>국민연금</th>
                        <th>고용보험</th>
                        <th>실지급액</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRow.salaries.map((sal, idx) => (
                        <tr key={idx}>
                          <td>{sal.name}</td>
                          <td style={{ textAlign: "right" }}>{sal.totalSalary?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{sal.incomeTax?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{sal.localTax?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{sal.health?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{sal.care?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{sal.pension?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{sal.employment?.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{sal.total?.toLocaleString()}</td>
                        </tr>
                      ))}
                      <tr className={styles.sumRow}>
                        <td>합계</td>
                        <td style={{ textAlign: "right" }}>
                          {selectedRow.salaries.reduce((a, b) => a + (b.totalSalary || 0), 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {selectedRow.salaries.reduce((a, b) => a + (b.incomeTax || 0), 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {selectedRow.salaries.reduce((a, b) => a + (b.localTax || 0), 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {selectedRow.salaries.reduce((a, b) => a + (b.health || 0), 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {selectedRow.salaries.reduce((a, b) => a + (b.care || 0), 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {selectedRow.salaries.reduce((a, b) => a + (b.pension || 0), 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {selectedRow.salaries.reduce((a, b) => a + (b.employment || 0), 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {selectedRow.salaries.reduce((a, b) => a + (b.total || 0), 0).toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button onClick={closeDetail} className={styles.btn}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}